import type { ChangeEvent, FC, ReactNode } from "react";
import styles from "@/components/DataTableCard/DataTableCard.module.css";

interface DataTableCardProps {
  children: ReactNode;
  csvButtonDisabled: boolean;
  csvButtonLabel: string;
  error: string | null;
  filterPlaceholder: string;
  globalFilter: string;
  isLoading: boolean;
  sectionLabel?: string;
  subtitle?: string;
  title: string;
  onExportCsv: () => void;
  onFilterChange: (e: ChangeEvent<HTMLInputElement>) => void;
}

const SKELETON_ROW_COUNT = 6;
const SKELETON_ROWS = Array.from(
  { length: SKELETON_ROW_COUNT },
  (_, index) => index
);

const DataTableCard: FC<DataTableCardProps> = ({
  children,
  csvButtonDisabled,
  csvButtonLabel,
  error,
  filterPlaceholder,
  globalFilter,
  isLoading,
  sectionLabel,
  subtitle,
  title,
  onExportCsv,
  onFilterChange,
}) => {
  let bodyContent: ReactNode;
  if (error) {
    bodyContent = <p className={styles.message}>{error}</p>;
  } else if (isLoading) {
    bodyContent = (
      <div aria-busy="true" className={styles.skeleton}>
        {SKELETON_ROWS.map((row) => (
          <div key={row} className={styles.skeletonRow} />
        ))}
      </div>
    );
  } else {
    bodyContent = children;
  }

  return (
    <section aria-label={title} className={styles.section}>
      {sectionLabel && <h2 className={styles.sectionLabel}>{sectionLabel}</h2>}
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <div className={styles.titleGroup}>
            <span className={styles.cardTitle}>{title}</span>
            {subtitle && (
              <span className={styles.cardSubtitle}>{subtitle}</span>
            )}
          </div>
          <div className={styles.controls}>
            <input
              aria-label={filterPlaceholder}
              className={styles.filterInput}
              placeholder={filterPlaceholder}
              value={globalFilter}
              onChange={onFilterChange}
            />
            <button
              className={styles.csvButton}
              disabled={csvButtonDisabled}
              type="button"
              onClick={onExportCsv}
            >
              {csvButtonLabel}
            </button>
          </div>
        </div>
        {bodyContent}
      </div>
    </section>
  );
};

export default DataTableCard;
